import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import '@adornis/calendar/calendar.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import '@chancenwerk/components/chancen-component-card-title.js';
import '@chancenwerk/design/components/cr-data-view-table.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-dropdown-search-selection.js';
import { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import { WorkTrackingTaetigkeit } from '@chancenwerk/work-tracking/entitites/WorkTrackingTaetigkeit.js';
import { WorkTrackingTaetigkeitenState } from '@chancenwerk/work-tracking/entitites/WorkTrackingTaetigkeitenState.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { takeUntil } from 'rxjs';
import { WorkTrackingAPI } from '../api/WorkTrackingAPI.js';

export type WorkTrackingTaetigkeitChange = { taetigkeit: WorkTrackingTaetigkeit; note: string };

@customElement('chancen-dialog-edit-work-tracking-taetigkeit')
export class DialogEditWorkTrackingTaetigkeit extends ChromiumDialog<WorkTrackingTaetigkeitChange> {
  static override get element_name() {
    return 'chancen-dialog-edit-work-tracking-taetigkeit';
  }

  @property({ attribute: false }) public overrideTitle = new RXController(this, null as Maybe<string>);
  @property({ attribute: false }) public types = new RXController(this, null as Maybe<string[]>);
  @property({ attribute: false }) public allowFreeType = new RXController(this, false);
  @property({ attribute: false }) public userID = null as Maybe<string>;
  @property({ attribute: false }) public fixedKPFs = null as Maybe<{ id: string; name: string }[]>;
  @property({ attribute: false }) public extended = new RXController(this, false);
  @property({ attribute: false }) public taetigkeit = new RXController<WorkTrackingTaetigkeit>(
    this,
    new WorkTrackingTaetigkeit({
      stateContractor: WorkTrackingTaetigkeitenState.OPEN,
      stateResponsible: WorkTrackingTaetigkeitenState.OPEN,
    }),
  );

  @state() private _form = new FormController(this, this.taetigkeit.observable, {
    onSuccess: doc => {
      this.close({
        taetigkeit: doc,
        note: this._note,
      });
    },
  });

  @state() private _note = '';

  override heading = 'Taetigkeit bearbeiten';

  override connectedCallback(): void {
    super.connectedCallback();

    this.overrideTitle.observable.pipe(takeUntil(this.disconnected)).subscribe(t => {
      if (t) {
        this.heading = t;
      }
    });

    this.taetigkeit.observable.pipe(takeUntil(this.disconnected)).subscribe(t => {
      if (this.overrideTitle.value) {
        return;
      }

      if (t._id) {
        this.heading = 'Taetigkeit bearbeiten';
      } else {
        this.heading = 'Neue Taetigkeit erstellen';
      }
    });
  }

  private _extended() {
    return html`
      <cr-input type="time" placeholder="Startzeit" ${this._form.field('startTime')}></cr-input>
      <cr-input type="time" placeholder="Endzeit" ${this._form.field('endTime')}></cr-input>
    `;
  }

  private _noteInput() {
    if (!this._form.document?._id) {
      return nothing;
    }

    return html`<cr-hr></cr-hr>
      <cr-input
        placeholder="Notiz"
        @value-picked=${(e: ValueEvent<string>) => (this._note = e.detail.value)}
      ></cr-input>`;
  }

  private _type() {
    if (!this.types.value) {
      return html` <cr-input placeholder="Art" ${this._form.field('type')}></cr-input> `;
    }

    const selection = html` <cr-dropdown-selection
      ${this._form.field('type')}
      .selectables=${this.types.value}
      placeholder="Art"
      clearable
      @value-picked=${(e: ValueEvent<Maybe<string>>) => {
        if (!this._form.document) {
          return;
        }
        this._form.document.type = e.detail.value;
        this.requestUpdate();
      }}
    ></cr-dropdown-selection>`;

    if (!this.allowFreeType.value) {
      return selection;
    }

    const free = html`
      <cr-input
        ${this._form.field('type')}
        clearable
        placeholder="Art-Freitext"
        @value-changed=${(e: ValueEvent<Maybe<string>>) => {
          if (!this._form.document) {
            return;
          }
          this._form.document.type = e.detail.value;
          this.requestUpdate();
        }}
      ></cr-input>
    `;

    if (!this._form.document?.type || this._form.document.type.length === 0) {
      return [selection, free];
    }

    if (this.types.value.includes(this._form.document?.type ?? '')) {
      return selection;
    } else {
      return free;
    }
  }

  override content() {
    return html`<cr-flex space="sm">
      ${this.taetigkeit.value._id
        ? nothing
        : html`<cr-date-picker-dropdown
            .end-year=${DateTime.now().plus({ years: 5 }).year}
            placeholder="wann?"
            click-to-save
            flex
            clearable
            ${this._form.field('date')}
          ></cr-date-picker-dropdown>`}
      ${this._type()} ${this.extended.value ? this._extended() : nothing}
      ${!this.fixedKPFs
        ? html`<x-entity-picker
            flex
            placeholder="Standort"
            .options=${{
              class: KPF,
              operation: WorkTrackingAPI.searchKPF,
              selectionSet: {
                name: 1,
                kuerzel: 1,
                produkt: { _class: 1, kuerzel: 1 },
                school: { name: 1, kuerzel: 1 },
              },
              filter: new AdornisFilter({}),
            }}
            ${this._form.field('kpfID')}
          ></x-entity-picker>`
        : html`<cr-dropdown-selection
            flex
            placeholder="KPF"
            .selectables=${this.fixedKPFs.map(kpf => kpf.id)}
            .value=${this._form.document?.kpfID}
            .renderString=${(kpf: Maybe<string>) => this.fixedKPFs?.find(k => k.id === kpf)?.name ?? ''}
            ${this._form.field('kpfID')}
          ></cr-dropdown-selection>`}
      <cr-input type="number" placeholder="Kosten" ${this._form.field('money')}></cr-input>
      ${this._noteInput()}
      <cr-button
        @click=${() => {
          if (this.userID && this._form.document) {
            this._form.document.userID = this.userID;
          }
          this._form.validate();
        }}
        >Speichern</cr-button
      >
    </cr-flex>`;
  }
}
